import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/utils/datetime";

export default function KGIncidents() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const [items, setItems] = useState<any[]>([]);
    const [people, setPeople] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const [inc, ppl] = await Promise.all([api.get("/kg/incidents"), api.get("/people?limit=200")]);
            setItems(inc.data.items);
            setPeople(ppl.data.items.filter((p: any) => (p.person_types || []).includes("student")));
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleNotified = async (id: string, notified: boolean) => {
        try { await api.patch(`/kg/incidents/${id}`, { parent_notified: notified }); load(); }
        catch (e: any) { setErr(formatApiError(e)); }
    };
    const setStatus = async (id: string, status: "open" | "closed") => {
        try { await api.patch(`/kg/incidents/${id}`, { status }); load(); }
        catch (e: any) { setErr(formatApiError(e)); }
    };

    if (!hasPerm("kg.view")) return <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text)" }} data-testid="kgi-perm-denied">{t("common.no_permission")}</div>;

    return (
        <div className="space-y-4" data-testid="kg-incidents-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("kg.incidents_title")}</h1>
                {hasPerm("kg.incident") && <button data-testid="kgi-new-btn" onClick={() => setCreating(true)} className="rounded-md px-3 py-2 text-sm inline-flex items-center gap-2" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}><Plus size={14}/>{t("kg.new_incident")}</button>}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {loading ? <div className="p-12 flex justify-center"><Loader2 className="animate-spin"/></div> : (
                <div className="surface-card overflow-hidden" data-testid="incidents-list">
                    {items.length === 0 ? <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                            {items.map(i => {
                                const child = people.find(p => p.id === i.child_person_id);
                                return (
                                    <li key={i.id} className="p-4" data-testid={`incident-${i.id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <span style={{ color: "var(--text)" }}>{child ? `${child.first_name} ${child.last_name}` : "—"}</span>
                                                <span className="ml-3 text-xs" style={{ color: "var(--text-muted)" }}>{fmtDateTime(i.at)}</span>
                                            </div>
                                            <span className={`status-pill ${i.status === "closed" ? "status-success" : "status-warning"}`}>{t(`kg.incident.status.${i.status}`)}</span>
                                        </div>
                                        <div className="text-sm mb-1" style={{ color: "var(--text)" }}>{i.description}</div>
                                        {i.action_taken && <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("kg.incident.action")}: {i.action_taken}</div>}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {hasPerm("kg.incident") && (
                                                <label className="text-xs flex items-center gap-1" style={{ color: "var(--text)" }}>
                                                    <input data-testid={`notify-${i.id}`} type="checkbox" checked={i.parent_notified} onChange={e => toggleNotified(i.id, e.target.checked)}/>
                                                    {t("kg.incident.parent_notified_toggle")}
                                                </label>
                                            )}
                                            {i.parent_notified_at && <span className="text-xs" style={{ color: "var(--text-muted)" }}>({fmtDateTime(i.parent_notified_at)})</span>}
                                            {hasPerm("kg.incident") && i.status === "open" && <button data-testid={`close-${i.id}`} onClick={() => setStatus(i.id, "closed")} className="text-xs rounded-md border px-2 py-1" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{t("kg.incident.close")}</button>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
            {creating && <NewIncidentDialog people={people} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }}/>}
        </div>
    );
}

function NewIncidentDialog({ people, onClose, onDone }: { people: any[]; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({ child_person_id: "", description: "", action_taken: "" });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
    useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
    const save = async () => {
        setBusy(true); setErr(null);
        try { await api.post("/kg/incidents", { child_person_id: form.child_person_id, description: form.description, action_taken: form.action_taken || null }); onDone(); }
        catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="new-incident-dialog">
            <div className="surface-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("kg.new_incident")}</h3>
                    <button onClick={onClose}><X size={18} style={{ color: "var(--text-muted)" }}/></button>
                </div>
                <select data-testid="inc-child" value={form.child_person_id} onChange={e => setForm({ ...form, child_person_id: e.target.value })} className="surface-input px-3 py-2 text-sm w-full mb-2" style={{ minHeight: 40 }}>
                    <option value="">{t("kg.pick_child")}</option>
                    {people.map((p: any) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
                <textarea data-testid="inc-desc" placeholder={t("kg.incident.description") as string} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="surface-input px-3 py-2 text-sm w-full mb-2" rows={3}/>
                <textarea data-testid="inc-action" placeholder={t("kg.incident.action") as string} value={form.action_taken} onChange={e => setForm({ ...form, action_taken: e.target.value })} className="surface-input px-3 py-2 text-sm w-full mb-3" rows={2}/>
                {err && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="inc-error">{err}</div>}
                <button data-testid="inc-confirm" disabled={busy || !form.child_person_id || !form.description} onClick={save} className="w-full rounded-md py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{busy ? <Loader2 className="animate-spin inline" size={14}/> : t("common.save")}</button>
            </div>
        </div>
    );
}
